import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ShieldCheck, FileText, Lock, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const DOC_ORDER = ['informed_consent', 'privacy_policy', 'terms_of_service'];
const DOC_ICONS = {
  informed_consent: ShieldCheck,
  privacy_policy: Lock,
  terms_of_service: FileText,
};

export default function ConsentWizard() {
  const { user, axiosAuth } = useAuth();
  const api = axiosAuth();
  const [open, setOpen] = useState(false);
  const [templates, setTemplates] = useState({});
  const [activeDoc, setActiveDoc] = useState('informed_consent');
  const [readDocs, setReadDocs] = useState({});
  const [typedName, setTypedName] = useState('');
  const [accepted, setAccepted] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!user) return;
    // Only require consent for clients/participants
    if (!['client', 'housing_resident'].includes(user.role)) return;
    (async () => {
      try {
        const statusRes = await api.get('/consent/status');
        if (statusRes.data.completed) return;
        const tplRes = await api.get('/consent/templates');
        setTemplates(tplRes.data);
        setOpen(true);
      } catch {
        // silent — never block app on consent fetch failure
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const allAccepted = DOC_ORDER.every(k => accepted[k]);
  const nameValid = typedName.trim().length >= 2;

  const handleSubmit = async () => {
    if (!allAccepted) return toast.error('Please accept all three documents');
    if (!nameValid) return toast.error('Type your full name to sign');
    setSubmitting(true);
    try {
      await api.post('/consent/sign', {
        typed_full_name: typedName.trim(),
        documents: DOC_ORDER,
      });
      toast.success('Welcome — consent recorded.');
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to record consent');
    }
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={() => {/* not dismissible */}}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden rounded-2xl" data-testid="consent-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <ShieldCheck className="text-teal-600" size={22} /> Before You Begin
          </DialogTitle>
          <DialogDescription>Please read and accept these three documents. You only need to do this once.</DialogDescription>
        </DialogHeader>

        <Tabs value={activeDoc} onValueChange={(v) => { setActiveDoc(v); setReadDocs(r => ({ ...r, [v]: true })); }}>
          <TabsList className="grid grid-cols-3 mb-3">
            {DOC_ORDER.map(k => {
              const Icon = DOC_ICONS[k];
              return (
                <TabsTrigger key={k} value={k} className="text-xs gap-1" data-testid={`consent-tab-${k}`}>
                  <Icon size={12} /> {templates[k]?.title?.split(' ').slice(0, 2).join(' ')}
                  {accepted[k] && <CheckCircle2 size={11} className="text-emerald-600 ml-0.5" />}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {DOC_ORDER.map(k => (
            <TabsContent key={k} value={k} className="mt-0">
              <Card className="border border-slate-200 rounded-xl p-0 overflow-hidden">
                <div className="p-4 border-b bg-slate-50">
                  <p className="font-semibold text-sm">{templates[k]?.title}</p>
                  <p className="text-[10px] text-slate-500">Version {templates[k]?.version || '1.0'}</p>
                </div>
                <ScrollArea className="h-56 p-4">
                  <pre className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">{templates[k]?.body}</pre>
                </ScrollArea>
                <div className="p-3 border-t bg-amber-50/50 flex items-start gap-2">
                  <Checkbox
                    id={`accept-${k}`}
                    checked={!!accepted[k]}
                    onCheckedChange={(c) => setAccepted(a => ({ ...a, [k]: !!c }))}
                    className="mt-0.5"
                    data-testid={`consent-checkbox-${k}`}
                  />
                  <label htmlFor={`accept-${k}`} className="text-xs cursor-pointer">
                    I have read and agree to the <strong>{templates[k]?.title}</strong>.
                  </label>
                </div>
              </Card>
            </TabsContent>
          ))}
        </Tabs>

        {/* Sign */}
        <div className="border-t pt-3 mt-2 space-y-2.5">
          <div className="flex items-center gap-2 text-xs">
            {allAccepted ? (
              <CheckCircle2 size={14} className="text-emerald-600" />
            ) : (
              <AlertCircle size={14} className="text-amber-600" />
            )}
            <span className={allAccepted ? 'text-emerald-700' : 'text-slate-600'}>
              {allAccepted ? 'All three documents accepted' : `${Object.values(accepted).filter(Boolean).length} of 3 accepted`}
            </span>
          </div>
          <div>
            <p className="text-xs font-semibold mb-1">Type your full legal name to sign:</p>
            <Input
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Your full name"
              data-testid="consent-typed-name"
              disabled={!allAccepted}
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!allAccepted || !nameValid || submitting}
            className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full"
            data-testid="consent-submit-btn"
          >
            {submitting ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
            Sign & Continue
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
